<template>
    <div
        v-editable="blok"
        class="faq-element"
    >
        <KSectionHeader
            v-if="blok.eyebrow || blok.title || blok.text"
            class="faq-element__header"
            :eyebrow="blok.eyebrow"
            :title="blok.title"
            :text="blok.text"
            :align="blok.align || 'start'"
        >
            <template
                v-if="blok.action_label && (blok.action_link?.url || blok.action_link?.email || blok.action_link?.cached_url || blok.action_link?.anchor)"
                #actions
            >
                <StoryblokSectionHeaderAction
                    :label="blok.action_label"
                    :link="blok.action_link"
                    placement="faqs"
                />
            </template>
        </KSectionHeader>

        <KAccordion
            :model-value="openItems"
            :items="accordionItems"
            :type="openMany ? 'multiple' : 'single'"
            @update:model-value="trackOpenedItem"
        >
            <template #default="{ index }">
                <div
                    class="k-prose"
                    v-html="renderRichText(blok.items[index].answer)"
                />
            </template>
        </KAccordion>
    </div>
</template>

<script setup>
import { KAccordion, KSectionHeader } from '@vemedia/karibu'
import './faqs.scss'

defineOptions({
    name: 'FaqElement',
})

const props = defineProps({
    blok: {
        type: Object,
        required: true,
    },
    openMany: {
        type: Boolean,
        default: true,
    },
})

const { capture } = useTracking()
const openItems = ref(props.openMany ? [] : undefined)
const accordionItems = computed(() => props.blok.items.map((item, index) => ({
    value: item._uid ?? String(index),
    title: item.question,
})))

function trackOpenedItem(value) {
    const current = Array.isArray(value) ? value : value ? [value] : []
    const previous = Array.isArray(openItems.value) ? openItems.value : openItems.value ? [openItems.value] : []
    const opened = current.find(item => !previous.includes(item))
    const index = accordionItems.value.findIndex(item => item.value === opened)

    if (index >= 0)
        capture('faq_opened', { question: props.blok.items[index].question })

    openItems.value = value
}

const structuredData = computed(() => {
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        'mainEntity': props.blok.items.map(item => ({
            '@type': 'Question',
            'name': item.question,
            'acceptedAnswer': {
                '@type': 'Answer',
                'text': renderRichText(item.answer).replace(/<[^>]*>?/g, ''),
            },
        })),
    }
})

useHead({
    script: [{
        key: `faq-json-ld-${props.blok._uid}`,
        type: 'application/ld+json',
        textContent: () => JSON.stringify(structuredData.value),
    }],
})
</script>
